import { Input } from 'common/components/Input/Input'
import { Select } from 'common/components/Select/Select'
import type { IOption } from 'common/interfaces/IOption'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { Col, Row } from 'antd'
import { useAuth } from 'app/providers/auth/AuthProvider'
import { Button } from 'common/components/Button/Button'
import { Modal } from 'common/components/Modal'
import PhoneInput from 'common/components/PhoneInput/PhoneInput'
import { Show } from 'common/components/Show/Show'
import { SpinnerOverlay } from 'common/components/SpinnerOverlay'
import { useFetchEmrProvidersQuery } from 'features/Patients/state/api/patientApi'
import { isValidPhoneNumber } from 'react-phone-number-input'
import styles from './patientFormModal.module.scss'

interface PatientFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (values: PatientFormType) => Promise<void>
  initialValues?: PatientFormType
  languages: IOption[]
}

const validationSchema = Yup.object({
  id: Yup.number().nullable().notRequired(),
  first_name: Yup.string().required('First name is required').min(1, 'Minimum 1 character'),
  last_name: Yup.string().required('Last name is required').min(1, 'Minimum 1 character'),
  phone: Yup.string()
    .nullable()
    .required('Please enter the mobile phone')
    .min(1, 'Please enter a valid mobile phone.')
    .test('is-valid-phone', 'Please enter a valid mobile phone', function (value) {
      return value && isValidPhoneNumber(value)
    }),

  language_type_code: Yup.number().required('Language is required').nullable(),
  doctor_emr_id: Yup.number().nullable().notRequired(),
})

// Applied only while the provider field is visible AND there are providers to
// pick from: a practice whose EMR has no mapped providers must still be able
// to create patients, so the base (optional) schema stays in force for them.
const providerRequiredSchema = validationSchema.shape({
  doctor_emr_id: Yup.number().required('Provider is required'),
})

export type PatientFormType = Yup.InferType<typeof validationSchema>

const defaultInitialValues: PatientFormType = {
  id: undefined,
  first_name: '',
  last_name: '',
  phone: '',
  language_type_code: null,
  // Undefined rather than null so an unpicked provider is dropped from the JSON
  // body entirely rather than sent as an explicit null.
  doctor_emr_id: undefined,
}

export const PatientFormModal: React.FC<PatientFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialValues,
  languages = [],
}) => {
  const { hasEmrEnabled, emrId } = useAuth()
  const isEdit = !!initialValues
  // Create-only: nothing returns a patient's existing provider, so on edit the
  // field could only ever render blank and overwrite what is already stored.
  const showDefaultProvider = hasEmrEnabled && !isEdit
  const { data: emrProviders = [], isFetching: isFetchingEmrProviders } = useFetchEmrProvidersQuery(
    undefined,
    { skip: !showDefaultProvider },
  )
  const isProviderRequired = showDefaultProvider && emrProviders.length > 0
  // The emr-providers response has no main-provider flag; the practice
  // profile's own emr_id IS the principal doctor's emr_provider_id (Jessica:
  // both 5), so matching it against the options preselects her. The find also
  // guards against an emr_id that is not a mapped provider.
  const defaultProviderId = emrProviders.find((provider) => provider.code === emrId)?.code
  const formInitialValues = initialValues ?? defaultInitialValues
  const { values, errors, isSubmitting, handleChange, handleSubmit, setFieldValue, resetForm } =
    useFormik({
      initialValues: {
        ...formInitialValues,
        language_type_code: languages?.[0]?.code,
        doctor_emr_id: formInitialValues.doctor_emr_id ?? defaultProviderId,
      } as PatientFormType,
      validationSchema: isProviderRequired ? providerRequiredSchema : validationSchema,
      enableReinitialize: true,
      validateOnChange: false,
      onSubmit: async (values, { setSubmitting }) => {
        try {
          await onSubmit(values as PatientFormType)
          handleClose()
        } catch (error) {
          console.error('Error submitting form:', error)
        } finally {
          setSubmitting(false)
        }
      },
    })

  function handleClose() {
    resetForm()
    onClose()
  }

  return (
    <Modal
      footer={null}
      open={isOpen}
      title={isEdit ? 'Edit Patient' : 'Add New Patient'}
      onCancel={handleClose}
      destroyOnHidden>
      <form onSubmit={handleSubmit} className={styles.form}>
        <SpinnerOverlay isLoading={isSubmitting} />
        <>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Input
                label='First Name'
                name='first_name'
                value={values.first_name}
                onChange={handleChange}
                error={errors.first_name}
                required
                tabIndex={1}
              />
            </Col>

            <Col xs={24} md={12}>
              <Input
                label='Last Name'
                name='last_name'
                value={values.last_name}
                onChange={handleChange}
                error={errors.last_name}
                required
                tabIndex={2}
              />
            </Col>

            <Col xs={24} md={12}>
              <PhoneInput
                label='Phone'
                value={values.phone}
                onChange={(value) => setFieldValue('phone', value)}
                required
                error={errors.phone}
                tabIndex={3}
              />
            </Col>

            <Col xs={24} md={12}>
              <Select
                label='Language'
                name='language_type_code'
                value={values.language_type_code}
                onChange={(value) => setFieldValue('language_type_code', value)}
                listOptions={languages}
                required
                error={errors.language_type_code}
                tabIndex={4}
              />
            </Col>

            <Show when={showDefaultProvider}>
              <Col xs={24} md={12}>
                <Select
                  label='Default provider'
                  name='doctor_emr_id'
                  value={values.doctor_emr_id}
                  onChange={(value) => setFieldValue('doctor_emr_id', value)}
                  listOptions={emrProviders}
                  loading={isFetchingEmrProviders}
                  error={errors.doctor_emr_id}
                  required={isProviderRequired}
                  tabIndex={5}
                />
              </Col>
            </Show>
          </Row>
          <Row justify='end' className={styles.submitButton}>
            <Button disabled={isSubmitting} type='primary' htmlType='submit'>
              {isEdit ? 'Update' : 'Add'}
            </Button>
          </Row>
        </>
      </form>
    </Modal>
  )
}
